/*
 * How loud the room is, as a number the waveform can draw.
 *
 * The recogniser in lib/speech.ts hears the words; it says nothing about
 * volume, and a visualiser that does not move when the customer speaks is a
 * visualiser that looks broken. So a second, silent listener opens the same
 * microphone through the Web Audio API and reads its level — nothing is
 * recorded, nothing leaves the device, the stream is closed the moment
 * listening ends.
 *
 * BEST-EFFORT, AND IT HAS TO BE. Some engines will not hand the microphone to
 * the Web Audio API while the recogniser holds it, some will prompt twice, and
 * a customer who refused the recogniser has refused this too. `openMeter()`
 * resolving to null is an ordinary outcome, and the caller draws a synthetic
 * pulse from the words arriving instead — see useAsk in components/FrontDoor.
 */

export interface Meter {
  /** The current level, 0 (silence) to 1 (loud speech). Cheap; call per frame. */
  read(): number
  /** Release the microphone. Idempotent. */
  close(): void
}

/**
 * Speech sits around 0.05–0.25 RMS on a phone microphone. Scaled so a normal
 * speaking voice fills most of the range and a shout clips at 1.
 */
const GAIN = 4

export async function openMeter(): Promise<Meter | null> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null

  const scope = window as Window & { webkitAudioContext?: typeof AudioContext }
  const Context = window.AudioContext ?? scope.webkitAudioContext
  if (!Context) return null

  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
  } catch {
    return null
  }

  let context: AudioContext
  try {
    context = new Context()
    // Safari creates it suspended until a gesture; the tap on the mic was one.
    if (context.state === 'suspended') void context.resume().catch(() => {})
  } catch {
    stream.getTracks().forEach((track) => track.stop())
    return null
  }

  const analyser = context.createAnalyser()
  analyser.fftSize = 512
  analyser.smoothingTimeConstant = 0.6
  context.createMediaStreamSource(stream).connect(analyser)

  const samples = new Uint8Array(analyser.fftSize)
  let closed = false

  return {
    read() {
      if (closed) return 0
      analyser.getByteTimeDomainData(samples)
      let sum = 0
      for (let index = 0; index < samples.length; index += 1) {
        const centred = ((samples[index] ?? 128) - 128) / 128
        sum += centred * centred
      }
      const rms = Math.sqrt(sum / samples.length)
      return Math.min(1, rms * GAIN)
    },
    close() {
      if (closed) return
      closed = true
      stream.getTracks().forEach((track) => track.stop())
      void context.close().catch(() => {})
    },
  }
}
